/**
 * Types for the Agent Memory module (Memory spec §10A) — the /v1/memory/* surfaces.
 * Distinct from the shipped Module-10 memory store types in `memory.ts`, which stay untouched.
 */

/** LifecycleStatus — the one publication ladder (TINYINT, base 1). */
export const LifecycleStatus = {
  Draft: 1,
  Active: 2,
  Deprecated: 3,
  Retired: 4,
} as const;
export type LifecycleStatusValue = (typeof LifecycleStatus)[keyof typeof LifecycleStatus];

/** MemoryImpactTier — the blast radius a rule claims (§8.3). */
export const MemoryImpactTier = {
  Operational: 1,
  Financial: 2,
  Clinical: 3,
} as const;
export type MemoryImpactTierValue = (typeof MemoryImpactTier)[keyof typeof MemoryImpactTier];

/** RuleAuthoringSource (§9.14) — origin confers no privilege. */
export const RuleAuthoringSource = {
  SmeAuthored: 1,
  ReflectionProposed: 2,
} as const;

/** One scope slot as declared on an AMD version (§2.2). */
export interface ScopeSlotDto {
  ordinal: number;
  dimensionKey: string | null;
  required: boolean;
}

/** Wire shape of one Agent Memory Definition version. */
export interface AgentMemoryDefinitionDto {
  agentMemoryDefinitionId: string;
  tenantId: string;
  projectId: string | null;
  agentKey: string;
  version: number;
  isCurrent: boolean;
  status: number;
  displayName: string;
  description: string | null;
  scopeSlots: ScopeSlotDto[];
  taskName: string;
  taskDescription: string;
  episodeSchemaJson: string;
  instructionsTokenBudget: number;
  episodicTokenBudget: number;
  minInjectionScore: number;
  candidateFetchLimit: number;
  hybridSearchEnabled: boolean;
  recencyHalfLifeDays: number;
  retrievalRefreshEveryNTurns: number;
  reflectionEnabled: boolean;
  owner: string | null;
  approvedBy: string | null;
  approvedAtUtc: string | null;
  effectiveFromUtc: string | null;
  changeReason: string | null;
  createdAtUtc: string;
  createdBy: string;
}

/** The detail view: head version plus history. */
export interface MemoryDefinitionDetailDto {
  definition: AgentMemoryDefinitionDto;
  versions: AgentMemoryDefinitionDto[];
}

/** The editable AMD surface. TenantId is never sent — it comes from the token. */
export interface MemoryDefinitionInput {
  displayName: string;
  description?: string | null;
  taskName: string;
  taskDescription: string;
  scopeSlots?: ScopeSlotDto[] | null;
  episodeSchemaJson?: string | null;
  instructionsTokenBudget?: number | null;
  episodicTokenBudget?: number | null;
  minInjectionScore?: number | null;
  candidateFetchLimit?: number | null;
  hybridSearchEnabled?: boolean | null;
  recencyHalfLifeDays?: number | null;
  retrievalRefreshEveryNTurns?: number | null;
  reflectionEnabled?: boolean | null;
  owner?: string | null;
  changeReason?: string | null;
}

/** Body of POST /v1/memory/definitions. */
export interface CreateMemoryDefinitionRequest {
  agentDefinitionId: string;
  definition: MemoryDefinitionInput;
  activate?: boolean;
  approvedBy?: string | null;
  approvedByRef?: string | null;
}

/** Body of PUT /v1/memory/definitions/{id} and POST …/versions. */
export interface MemoryDefinitionVersionRequest {
  definition: MemoryDefinitionInput;
  activate?: boolean;
  approvedBy?: string | null;
  approvedByRef?: string | null;
}

/** Body of POST /v1/memory/definitions/{id}/bulk-apply. */
export interface BulkApplyRequest {
  agentDefinitionIds: string[];
  activate?: boolean;
  approvedBy?: string | null;
  approvedByRef?: string | null;
}

/** The outcome of a bulk apply. */
export interface BulkApplyResultDto {
  created: AgentMemoryDefinitionDto[];
  skipped: string[];
}

/** Body of POST /v1/memory/definitions/{id}/preview. */
export interface PreviewMemoryContextRequest {
  declaredScope?: Record<string, string> | null;
  query?: string;
  turnIndex?: number;
}

/** One episodic block on a preview/runtime response. Role is always "user". */
export interface MemoryContextMessageDto {
  role: string;
  content: string;
}

/** One injected memory ref on a preview/runtime response. */
export interface InjectedMemoryDto {
  memoryRecordId: number;
  type: string;
  ruleFamilyId?: string | null;
  version?: number | null;
  score: number;
}

/** The §10A.2 retrieval response — instructions and messages stay distinct. */
export interface MemoryContextResponseDto {
  contextId?: string | null;
  instructions?: string | null;
  messages: MemoryContextMessageDto[];
  injected: InjectedMemoryDto[];
  degradation: string;
}

/** One injection-audit row for a preview run. */
export interface InjectionAuditDto {
  injectionAuditId: number;
  contextId: string;
  agentMemoryDefinitionId: string;
  memoryRecordId: number;
  memoryType: number;
  ruleFamilyId?: string | null;
  ruleVersion?: number | null;
  rankPosition: number;
  definitionKey?: string | null;
  definitionVersion?: number | null;
  verdict: string;
  score?: number | null;
  threshold?: number | null;
  errorCode?: string | null;
  isDecisive: boolean;
  wasSkipped: boolean;
  durationMs?: number | null;
  occurredAtUtc: string;
  agentSessionId?: string | null;
}

/** The effective tenant memory policy — read-only invariants view. */
export interface TenantMemoryPolicyDto {
  tenantId: string;
  policyVersion: number;
  effectiveFromUtc: string;
  approvedBy: string;
  changeReason: string;
  anonymizerEnabled: boolean;
  proceduralRequiresHumanApproval: boolean;
  tenantIsolationEnforced: boolean;
  agentMemoryIsolationEnforced: boolean;
  maxInstructionsTokenBudget: number;
  maxEpisodicTokenBudget: number;
  maxNegativeEpisodeFraction: number;
  minSamplingFloor: number;
  maxSamplingFloor: number;
  maxEvalHoldoutRate: number;
  reflectionAllowed: boolean;
}

/** One procedural rule version with its content record. */
export interface ProceduralRuleDto {
  memoryRecordId: number;
  ruleFamilyId: string;
  version: number;
  isCurrent: boolean;
  status: number;
  authoringSource: number;
  impactTier: number;
  ruleText: string;
  triggerJson: string;
  rationaleText: string | null;
  owner: string | null;
  approvedBy: string | null;
  approvedByRef: string | null;
  approvedAtUtc: string | null;
  retiredReason: string | null;
  retiredAtUtc: string | null;
  successCount: number;
  failureCount: number;
  createdAtUtc: string;
  createdBy: string | null;
}

/** Body of POST /v1/memory/rules. */
export interface CreateRuleRequest {
  agentMemoryDefinitionId: string;
  ruleText: string;
  triggerJson?: string | null;
  rationaleText?: string | null;
  impactTier?: number;
  declaredScope?: Record<string, string> | null;
  reason?: string | null;
}

/** Body of POST /v1/memory/rules/{familyId}/versions. */
export interface CreateRuleVersionRequest {
  agentMemoryDefinitionId: string;
  ruleText: string;
  triggerJson?: string | null;
  rationaleText?: string | null;
  impactTier?: number | null;
  declaredScope?: Record<string, string> | null;
  reason?: string | null;
}

/** One review-queue candidate (empty until the Phase 3 write path lands). */
export interface MemoryCandidateDto {
  memoryCandidateId: number;
  agentMemoryDefinitionId: string;
  memoryType: number;
  content: string;
  structureJson: string | null;
  pipelineState: number;
  anonymizationState: number;
  impactTier: number | null;
  reviewSlaUtc: string | null;
  createdAtUtc: string;
  agentSessionId: string | null;
  samplingStratum: string | null;
  /** Which anonymizer checks failed — names only, never values. */
  verificationFailures: string | null;
}

/** MemoryPipelineState values (candidate lifecycle). */
export const MemoryPipelineState = {
  New: 0,
  Anonymized: 1,
  Consolidating: 2,
  Promoted: 3,
  Merged: 4,
  Discarded: 5,
  PendingReview: 6,
  Approved: 7,
  Rejected: 8,
  Expired: 9,
} as const;

/** AnonymizationState values — a separate axis; quarantine is not a pipeline state. */
export const AnonymizationState = {
  Pending: 0,
  Passed: 1,
  Quarantined: 2,
  Discarded: 3,
} as const;

/** Counts only — an erasure result never echoes the identifier or content (§7.3.1). */
export interface MemoryErasureResultDto {
  candidatesErased: number;
  outboxRowsDeleted: number;
  recordsRetracted: number;
  stubsDeleted: number;
  episodesUnlinked: number;
  fieldValuesUnlinked: number;
}

/** Human label for a MemoryPipelineState value. */
export const pipelineStateLabel = (state: number): string => {
  switch (state) {
    case MemoryPipelineState.New: return 'New';
    case MemoryPipelineState.Anonymized: return 'Anonymized';
    case MemoryPipelineState.Consolidating: return 'Consolidating';
    case MemoryPipelineState.Promoted: return 'Promoted';
    case MemoryPipelineState.Merged: return 'Merged';
    case MemoryPipelineState.Discarded: return 'Discarded';
    case MemoryPipelineState.PendingReview: return 'Pending review';
    case MemoryPipelineState.Approved: return 'Approved';
    case MemoryPipelineState.Rejected: return 'Rejected';
    case MemoryPipelineState.Expired: return 'Expired';
    default: return `State ${state}`;
  }
};

/** One scope key/value pair filed on a pipeline row. */
export interface ScopePairDto {
  key: string;
  value: string;
}

/**
 * One Memory.ExtractionOutboxes row for the Memory Pipeline page. States and reasons only —
 * the API never returns transcript content on this surface.
 */
export interface ExtractionOutboxDto {
  outboxId: number;
  agentSessionId: string;
  agentMemoryDefinitionId: string;
  definitionVersion: number;
  turnIndex: number;
  /** Pending | Deferred | Claimed | Extracted | Skipped | Failed. */
  outboxState: string;
  outcome: string;
  scope: ScopePairDto[];
  pAdmit: number;
  pExtract: number;
  samplingStratum: string | null;
  lastError: string | null;
  hadToolError: boolean;
  hadUserCorrection: boolean;
  isHumanFlagged: boolean;
  selfAssessedConcern: boolean;
  isEvalHoldout: boolean;
  qaStratum: string | null;
  pQa: number | null;
  deferredUntilUtc: string | null;
  sessionEndedAtUtc: string;
  createdAtUtc: string;
  processedAtUtc: string | null;
  transcriptRef: string | null;
}

/** GET /v1/memory/sessions/{id}/outcome — current late-bound label. */
export interface SessionMemoryOutcomeDto {
  found: boolean;
  agentSessionId: string;
  agentMemoryDefinitionId: string | null;
  outcome: string | null;
  outcomeSource: string | null;
  failureReason: string | null;
  outcomeRevisionCount: number;
  hasEpisode: boolean;
  isDeferred: boolean;
  hasStub: boolean;
}

/** POST /v1/memory/sessions/{id}/outcome receipt. */
export interface OutcomeReceiptDto {
  accepted: boolean;
  agentSessionId: string;
  outcomeRevisionCount: number;
  forcedExtraction: boolean;
  agentMemoryDefinitionId: string | null;
  outcome: string | null;
  reason: string | null;
}

/** One procedural version that was live at asOf (§10A.5). */
export interface ReconstructedRuleDto {
  memoryRecordId: number;
  ruleFamilyId: string;
  version: number;
  ruleText: string;
  status: string;
  recordState: string;
  validFromUtc: string;
  validToUtc: string | null;
  wasInjected: boolean;
}

/** GET /v1/memory/audit/decisions — session reconstruction. */
export interface DecisionReconstructionDto {
  sessionId: string;
  asOfUtc: string;
  injections: InjectionAuditDto[];
  rulesAtAsOf: ReconstructedRuleDto[];
}

/** One append-only §12.2 lift window. */
export interface RuleEfficacyDto {
  ruleFamilyId: string;
  version: number;
  measuredThroughUtc: string;
  injectedRuns: number;
  comparisonRuns: number;
  weightedLift: number;
  liftCiLow: number;
  liftCiHigh: number;
}

export const SESSION_OUTCOMES = ['Completed', 'Incomplete', 'Incorrect', 'Abandoned'] as const;
export const OUTCOME_SOURCES = ['SystemVerified', 'UserConfirmed', 'DownstreamSignal', 'HumanReviewed'] as const;

/**
 * The configurable scope dimensions with their permanent registry ordinals (§2.2 seed).
 * Slot 5 is always UserId and never configurable.
 */
export const SCOPE_DIMENSIONS: { ordinal: number; key: string }[] = [
  { ordinal: 1, key: 'Payor' },
  { ordinal: 2, key: 'Practitioner' },
  { ordinal: 3, key: 'LineOfBusiness' },
  { ordinal: 4, key: 'Program' },
];

/** Human label for a LifecycleStatus value. */
export const lifecycleStatusLabel = (status: number): string => {
  switch (status) {
    case LifecycleStatus.Draft: return 'Draft';
    case LifecycleStatus.Active: return 'Active';
    case LifecycleStatus.Deprecated: return 'Deprecated';
    case LifecycleStatus.Retired: return 'Retired';
    default: return 'Unknown';
  }
};

/** Human label for a MemoryImpactTier value. */
export const impactTierLabel = (tier: number | null): string => {
  switch (tier) {
    case MemoryImpactTier.Operational: return 'Operational';
    case MemoryImpactTier.Financial: return 'Financial';
    case MemoryImpactTier.Clinical: return 'Clinical';
    default: return '—';
  }
};
